/**
 * Choosing a product's options in the product dialog: size, modifier groups ("2 gratis sauzen"),
 * ingredients to leave out and the "Groenten" shortcut. These functions only guide the cashier;
 * the server checks the same rules again and computes every price.
 */
import type { Channel, LineRequest, Menu, MenuRules, ModifierGroup, ModifierItem, Product } from "../../api/types";
import { eurosToCents, type Cents } from "../../lib/money";
import { toLineRequest } from "./cart";

export interface Selection {
  variant: string | undefined;
  /** Group id → picked product ids, in tap order (the order decides which picks are free). */
  choices: Record<string, string[]>;
  without: string[];
  /** "Enkel" is on: the ingredients tapped are the only ones the customer wants (Furkan, 09/10/2026). */
  onlyMode: boolean;
  /** The ingredients kept with "Enkel", in tap order. */
  only: string[];
  quantity: number;
  note: string;
}

/** A new choice for a product: the size it starts with (defaultVariant) and the quantity typed (2DP). */
export function newSelection(product: Product, variant?: string, quantity = 1): Selection {
  return {
    variant: variant ?? (product.variants.length === 1 ? product.variants[0]!.label : undefined),
    choices: {},
    without: [],
    onlyMode: false,
    only: [],
    quantity,
    note: "",
  };
}

/**
 * A ticket line opened again (F8, double click). A product with one size has no size on its line (see
 * lineFor), so the window starts with that one size, else it could never be saved.
 */
export function selectionFromLine(product: Product, line: LineRequest): Selection {
  return {
    variant: line.variant ?? (product.variants.length === 1 ? product.variants[0]!.label : undefined),
    choices: line.choices ?? {},
    without: line.without ?? [],
    onlyMode: (line.only ?? []).length > 0,
    only: line.only ?? [],
    quantity: line.quantity,
    note: line.note ?? "",
  };
}

export function groupsOf(menu: Menu, product: Product): ModifierGroup[] {
  return (product.modifierGroupIds ?? []).flatMap((id) => menu.modifierGroups[id] ?? []);
}

/** The options of a group offered for this kind of order ("Gesneden" only at the table). */
export function itemsFor(group: ModifierGroup, channel: Channel): ModifierItem[] {
  return group.items.filter((item) => item.channels === undefined || item.channels.includes(channel));
}

export type Section = { kind: "group"; group: ModifierGroup } | { kind: "without" };

/**
 * The parts of the product dialog in their order (prototype buildGroups): the product's groups, with
 * "Zonder" (from the ingredients) right before the first instruction, otherwise at the end.
 */
export function sectionsOf(menu: Menu, product: Product): Section[] {
  const sections: Section[] = groupsOf(menu, product).map((group) => ({ kind: "group", group }));
  if ((product.ingredients ?? []).length === 0) return sections;
  const instruction = sections.findIndex((section) => section.kind === "group" && section.group.kind === "INSTRUCTION");
  sections.splice(instruction < 0 ? sections.length : instruction, 0, { kind: "without" });
  return sections;
}

/** Needs the dialog, or can one tap add it (a drink with one size and no options)? */
export function needsDialog(menu: Menu, product: Product): boolean {
  return product.variants.length > 1 || groupsOf(menu, product).length > 0 || (product.ingredients ?? []).length > 0;
}

/** Most picks allowed in a group; Infinity when unlimited. */
export function maximumOf(group: ModifierGroup): number {
  if (group.selectionMode === "SINGLE") return 1;
  return group.maximum === 0 ? Number.POSITIVE_INFINITY : group.maximum;
}

/**
 * Price shown on an option chip for the chosen size, or null when the option does not exist for
 * that size (then the chip is disabled). Instructions are always free.
 */
export function optionPrice(group: ModifierGroup, item: ModifierItem, variant: string | undefined): Cents | null {
  if (group.kind === "INSTRUCTION") return 0;
  if (item.pricesBySize) {
    if (variant === undefined) return eurosToCents(Math.min(...Object.values(item.pricesBySize)));
    const euros = item.pricesBySize[variant];
    return euros === undefined ? null : eurosToCents(euros);
  }
  if (item.priceAdjustment)
    return item.priceAdjustment.method === "SET_AMOUNT" ? eurosToCents(item.priceAdjustment.value) : null;
  return 0;
}

export function toggleChoice(selection: Selection, group: ModifierGroup, productId: string): Selection {
  const picked = selection.choices[group.id] ?? [];
  let next: string[];
  if (picked.includes(productId)) next = picked.filter((id) => id !== productId);
  else if (group.selectionMode === "SINGLE") next = [productId];
  else if (picked.length >= maximumOf(group))
    return selection; // full: tap another one off first
  else next = [...picked, productId];
  return { ...selection, choices: { ...selection.choices, [group.id]: next } };
}

/** Changing the size drops picks that do not exist for the new size. */
export function chooseVariant(menu: Menu, product: Product, selection: Selection, variant: string): Selection {
  const choices = Object.fromEntries(
    groupsOf(menu, product).map((group) => [
      group.id,
      (selection.choices[group.id] ?? []).filter((id) => {
        const item = group.items.find((candidate) => candidate.productId === id);
        return item !== undefined && optionPrice(group, item, variant) !== null;
      }),
    ]),
  );
  return { ...selection, variant, choices };
}

export function toggleWithout(selection: Selection, ingredient: string): Selection {
  const without = selection.without.includes(ingredient)
    ? selection.without.filter((item) => item !== ingredient)
    : [...selection.without, ingredient];
  return { ...selection, without };
}

/**
 * The small "Enkel" button next to "Zonder": on, a tap on an ingredient means "only this one" and the
 * receipt says "Enkel sla"; what was left out one by one is forgotten, both together cannot be meant.
 * Off again, the ingredients come back as on the card.
 */
export function toggleOnlyMode(selection: Selection): Selection {
  return selection.onlyMode
    ? { ...selection, onlyMode: false, only: [] }
    : { ...selection, onlyMode: true, only: [], without: [] };
}

/** A tap on an ingredient with "Enkel" on: it is kept, or not any more. */
export function toggleOnly(selection: Selection, ingredient: string): Selection {
  const only = selection.only.includes(ingredient)
    ? selection.only.filter((item) => item !== ingredient)
    : [...selection.only, ingredient];
  return { ...selection, only };
}

/** "Groenten" replaces the separate vegetables: they are shown struck through and not sent twice. */
export function toggleAllVegetables(selection: Selection, rules: MenuRules): Selection {
  if (selection.without.includes(rules.allVegetables)) {
    return { ...selection, without: selection.without.filter((item) => item !== rules.allVegetables) };
  }
  return {
    ...selection,
    without: [rules.allVegetables, ...selection.without.filter((item) => !rules.vegetables.includes(item))],
  };
}

/**
 * Whether the product has the "Groenten" button: the server says for which products (at least three
 * vegetables). A product whose card only says "met groenten" has "Groenten" as an ingredient of its
 * own instead, left out like any other.
 */
export function offersAllVegetables(product: Product, rules: MenuRules): boolean {
  return rules.withoutVegetables.includes(product.id);
}

/** A vegetable that the "Groenten" button already takes away: shown struck through, "weg" (prototype zOff). */
export function coveredByAllVegetables(
  selection: Selection,
  product: Product,
  rules: MenuRules,
  ingredient: string,
): boolean {
  return (
    offersAllVegetables(product, rules) &&
    selection.without.includes(rules.allVegetables) &&
    rules.vegetables.includes(ingredient)
  );
}

/**
 * A tap on an ingredient under "Zonder" (prototype zTap). With "Groenten" on, a tap on one of the
 * vegetables means "alles behalve": every other vegetable of the product stays away, this one comes
 * back. Otherwise the ingredient goes away or comes back.
 */
export function tapWithout(selection: Selection, product: Product, rules: MenuRules, ingredient: string): Selection {
  if (!coveredByAllVegetables(selection, product, rules, ingredient)) return toggleWithout(selection, ingredient);
  const others = (product.ingredients ?? []).filter(
    (item) => item !== ingredient && rules.vegetables.includes(item) && !selection.without.includes(item),
  );
  return {
    ...selection,
    without: [...selection.without.filter((item) => item !== rules.allVegetables), ...others],
  };
}

/** Groups that still need a pick, in menu order. */
export function missingGroups(menu: Menu, product: Product, selection: Selection): ModifierGroup[] {
  return groupsOf(menu, product).filter((group) => (selection.choices[group.id] ?? []).length < group.minimum);
}

export function isComplete(menu: Menu, product: Product, selection: Selection): boolean {
  return selection.variant !== undefined && missingGroups(menu, product, selection).length === 0;
}

export function toLine(product: Product, selection: Selection): LineRequest {
  return toLineRequest({
    productId: product.id,
    quantity: selection.quantity,
    ...(selection.variant !== undefined && { variant: selection.variant }),
    choices: selection.choices,
    // "Enkel" without a tap changes nothing; on the receipt the ingredients follow the card
    ...(selection.onlyMode
      ? { only: (product.ingredients ?? []).filter((ingredient) => selection.only.includes(ingredient)) }
      : { without: selection.without }),
    note: selection.note,
  });
}
